import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import LargePill from 'ext-ui/components/LargePill/LargePill'
import Pill from 'ext-ui/components/Pill/Pill'
import { spacing } from 'ext-ui/style/spacing'
import React from 'react'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { OrgIntegration } from 'src/app/OrganizationIntegrations/OrganizationIntegrations'
import BetaTag from 'src/components/BetaTag/BetaTag'
import Box from 'src/components/Box/Box'
import IntegrationConnectedToOtherServiceTooltip from 'src/components/IntegrationConnectedToOtherServiceTooltip/IntegrationConnectedToOtherServiceTooltip'
import SvgAlertCircledFilled from 'src/components/Svgs/SvgAlertCircledFilled'
import SvgCheckFilled from 'src/components/Svgs/SvgCheckFilled'
import SvgLinked from 'src/components/Svgs/SvgLinked'
import SvgRecurring from 'src/components/Svgs/SvgRecurring'
import SvgUpdate from 'src/components/Svgs/SvgUpdate'
import {
  OrganizationConnectedPlatform,
  OrganizationIntegrationStatus,
} from 'src/enums'
import { useHasSaasEntitlement } from 'src/hooks/useHasSaasEntitlement'
import { useSaasUpgradeAction } from 'src/hooks/useSaasUpgradeAction'
import { useTranslate } from 'src/localization/Trns'
import { getSaasTierLabel } from 'src/models/organizations.models'
import Lnk from 'src/navigation/Lnk'
import {
  selectIsCspAuthenticatedFromState,
  selectUserListModelFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectFormattedOrgIntegrationLastSyncDate } from 'src/redux/selectors/data/orgIntegrations.selectors'
import { selectBrandColorCta } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

interface OrgIntegrationBoxProps {
  integration: OrgIntegration
  loading: boolean
}

const OrgIntegrationBox: React.FC<
  React.PropsWithChildren<OrgIntegrationBoxProps>
> = (props): React.ReactElement => {
  const translate = useTranslate()
  const { analytics } = useAnalyticsContext()
  const brandColorCta = useSelector(selectBrandColorCta)
  const isCspAuthenticated = useSelector(selectIsCspAuthenticatedFromState)
  const lastSyncDate = useSelector(selectFormattedOrgIntegrationLastSyncDate)
  const user = useSelector(selectUserListModelFromState)

  const { upgradeRequired, requiredTier } = useHasSaasEntitlement({
    feature: props.integration.feature,
  })

  const saasUpgradeAction = useSaasUpgradeAction(
    props.integration.organizationId,
  )

  const isDisabledDueToTier = upgradeRequired

  const isInteractable =
    !isCspAuthenticated &&
    props.integration.status !==
      OrganizationIntegrationStatus.CONNECTED_TO_OTHER_SERVICE &&
    user?.hasOrgAdminPermissions &&
    !isDisabledDueToTier
  const statusImage = (
    status: OrganizationIntegrationStatus | undefined,
  ): React.ReactElement => {
    const connect = (
      <SvgLinked
        css={css`
          transform: scale(calc(16 / 24));
        `}
      />
    )

    const connected = (
      <SvgCheckFilled
        css={css`
          transform-origin: left;
          margin-right: 1px;
        `}
      />
    )

    const incompleteSync = (
      <SvgAlertCircledFilled
        css={css`
          transform-origin: left;
          transform: scale(calc(16 / 24));
        `}
      />
    )

    const loading = (
      <Processing
        css={css`
          transform: scale(0.75);
        `}
      />
    )

    if (props.loading) {
      return loading
    }

    switch (status) {
      case OrganizationIntegrationStatus.ACTIVE:
        return connected
      case OrganizationIntegrationStatus.WAITING_FOR_INITIAL_PULL:
      case OrganizationIntegrationStatus.WAITING_TO_SYNC:
      case OrganizationIntegrationStatus.PENDING_CONFIGURATION:
        return incompleteSync
      case OrganizationIntegrationStatus.CONNECTED_TO_OTHER_SERVICE:
      case OrganizationIntegrationStatus.LACK_PERMISSIONS:
      default:
        return connect
    }
  }

  const statusLabel = (integration: OrgIntegration | undefined): string => {
    if (
      integration?.connectedPlatform === OrganizationConnectedPlatform.AMEX &&
      props.loading
    ) {
      return 'Connecting...'
    }

    switch (integration?.status) {
      case OrganizationIntegrationStatus.ACTIVE:
        return 'Connected'
      case OrganizationIntegrationStatus.WAITING_FOR_INITIAL_PULL:
      case OrganizationIntegrationStatus.WAITING_TO_SYNC:
      case OrganizationIntegrationStatus.PENDING_CONFIGURATION:
        return 'Connection Incomplete'
      case OrganizationIntegrationStatus.CONNECTED_TO_OTHER_SERVICE:
      case OrganizationIntegrationStatus.LACK_PERMISSIONS:
      default:
        return 'Connect'
    }
  }

  const rightBottomElement = (): React.ReactElement => {
    switch (props.integration.status) {
      case OrganizationIntegrationStatus.CONNECTED_TO_OTHER_SERVICE:
        return props.integration.connectedPlatform ===
          OrganizationConnectedPlatform.AMEX ? (
          <></>
        ) : (
          <IntegrationConnectedToOtherServiceTooltip
            openLeft
            showToolTip={false}
          />
        )
      case OrganizationIntegrationStatus.ACTIVE: {
        return props.integration.connectedPlatform ===
          OrganizationConnectedPlatform.AMEX ? (
          <span
            css={css`
              display: flex;
              align-items: center;
              ${getFontCss(
                14,
                styleConstants.colors.red_dark,
                styleConstants.fontFamilies.medium,
              )};
              padding-left: 1px;
            `}
          >
            {translate('Disconnect')}
          </span>
        ) : (
          <span
            css={css`
              display: flex;
              align-items: center;
              ${getFontCss(
                14,
                themeConstants.colors.cta,
                styleConstants.fontFamilies.medium,
              )};
              padding-left: 1px;
            `}
          >
            {translate('Manage')}
            <SvgUpdate
              css={css`
                margin-left: 4px;
                transform-origin: left;
                transform: scale(calc(16 / 24));
              `}
            />
          </span>
        )
      }
      case OrganizationIntegrationStatus.LACK_PERMISSIONS:
      case OrganizationIntegrationStatus.WAITING_FOR_INITIAL_PULL:
      case OrganizationIntegrationStatus.WAITING_TO_SYNC:
      case OrganizationIntegrationStatus.PENDING_CONFIGURATION:
      default: {
        return <></>
      }
    }
  }

  const content = (
    <div
      css={css`
        position: relative;
        height: 100%;
        z-index: 0;
        &:hover,
        &:focus-within {
          z-index: 2;
        }
        ${props.integration.showBetaTag &&
        css`
          z-index: 1;
        `}
      `}
      role={isInteractable ? 'button' : undefined}
      data-testid={`${props.integration.title.replace(/\s+/g, '-').toLowerCase()}-box`}
      aria-disabled={!isInteractable}
      onClick={
        isInteractable
          ? (e) => {
              props.integration.onClick?.(e)
              analytics?.track(
                'integrations - select ' +
                  props.integration.ariaLabelSource +
                  ' - box press',
                {
                  intent: 'select integration',
                  interaction: 'box press',
                },
              )
            }
          : undefined
      }
      onKeyPress={(): void => {
        return
      }}
      tabIndex={isInteractable ? 0 : undefined}
    >
      <Box
        css={css`
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: 350px;
          ${getBreakpointDown(breakpointSizes.SM)(css`
            width: 100%;
            max-width: 100%;
          `)}

          /* Flat design for new org settings */
          background-color: ${isDisabledDueToTier ? '#f9f9fd' : '#ffffff'};
          border: 1px solid #dadbe0;
          border-radius: 8px;
          box-shadow: none;
          transition: border-color 150ms ease-out;
          ${isInteractable
            ? css`
                cursor: pointer;
                &:hover {
                  border-color: ${brandColorCta};
                }
              `
            : css`
                cursor: default;
              `}
        `}
      >
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          `}
        >
          {props.integration.iconElement ? (
            <div
              css={css`
                width: 48px;
                height: 48px;
                margin-bottom: ${spacing(7)};
                display: flex;
                align-items: center;
                justify-content: center;
                ${isInteractable
                  ? css``
                  : css`
                      opacity: 0.5;
                    `}
              `}
            >
              {props.integration.iconElement}
            </div>
          ) : (
            <img
              css={css`
                width: 48px;
                height: 48px;
                margin-bottom: ${spacing(7)};
                ${isInteractable
                  ? css``
                  : css`
                      opacity: 0.5;
                    `}
              `}
              src={props.integration.iconSrc}
              alt={''}
            />
          )}
          {props.integration.status === OrganizationIntegrationStatus.ACTIVE &&
            props.integration.connectedPlatform !==
              OrganizationConnectedPlatform.AMEX && (
              <span
                css={css`
                  display: flex;
                  align-items: flex-start;
                `}
              >
                <SvgRecurring
                  fill={styleConstants.colors.gray_darkest}
                  css={css`
                    transform-origin: top;
                    transform: scale(calc(16 / 24));
                  `}
                />
                <span
                  css={css`
                    width: 115px;
                    ${getFontCss(
                      14,
                      styleConstants.colors.gray_darkest,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {translate('Last refreshed on ${date}', {
                    date: lastSyncDate,
                  })}
                </span>
              </span>
            )}
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: ${spacing(5)};
            flex-grow: 1;
          `}
        >
          <div className={'flex-column gap-5'}>
            <div
              css={css`
                position: relative;
                display: flex;
                align-items: center;
              `}
            >
              <h5
                css={css`
                  display: flex;
                  align-items: center;
                  font-weight: 500;
                  ${getFontCss(
                    20,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};
                  ${isInteractable
                    ? css``
                    : css`
                        opacity: 0.5;
                      `}
                `}
              >
                {props.integration.title}
                {props.integration.feature &&
                  requiredTier &&
                  upgradeRequired && (
                    <Pill
                      color="saas"
                      css={css`
                        margin-left: ${spacing(2)};
                      `}
                    >
                      {getSaasTierLabel(requiredTier)}
                    </Pill>
                  )}
              </h5>
              {props.integration.showBetaTag && (
                <div
                  css={css`
                    margin-left: ${spacing(3)};
                    transform: scale(0.85);
                  `}
                >
                  <BetaTag grayed={!isInteractable} />
                </div>
              )}
            </div>
            <p
              css={css`
                ${isInteractable
                  ? css``
                  : css`
                      opacity: 0.5;
                    `}
              `}
            >
              {props.integration.subtitle}
            </p>
          </div>
          {props.integration.feature && requiredTier && upgradeRequired && (
            <LargePill
              title={translate('Upgrade to Extend ${tier}', {
                tier: getSaasTierLabel(requiredTier),
              })}
              onClick={() => {
                saasUpgradeAction(
                  requiredTier,
                  `${props.integration.connectedPlatform} integration upgrade, ${requiredTier}`,
                )
              }}
            />
          )}
        </div>
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            margin-top: auto;
          `}
        >
          {!isDisabledDueToTier && (
            <span
              css={css`
                display: flex;
                align-items: center;
                gap: 5px;
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
                ${statusLabel(props.integration) === undefined &&
                css`
                  flex-direction: row-reverse;
                `}
                ${isInteractable
                  ? css``
                  : css`
                      opacity: 0.5;
                    `}
              `}
            >
              {statusImage(props.integration.status)}
              {translate(statusLabel(props.integration))}
            </span>
          )}
          {user?.hasOrgAdminPermissions &&
            !isDisabledDueToTier &&
            rightBottomElement()}
        </div>
      </Box>
    </div>
  )

  return (props.integration.to !== undefined ||
    props.integration.href !== undefined) &&
    isInteractable ? (
    <Lnk
      {...(props.integration.to !== undefined
        ? { to: props.integration.to }
        : { href: props.integration.href })}
      {...(props.integration.ariaLabelSource !== undefined
        ? { ariaLabelSource: props.integration.ariaLabelSource }
        : { ariaLabel: props.integration.ariaLabel })}
    >
      {content}
    </Lnk>
  ) : (
    content
  )
}

export default OrgIntegrationBox
